import { isAxiosError } from "axios"
import { useCallback, useState } from "react"
import { useSmoothApi } from "src/hooks/useSmoothApi"
import { SMOOTH_SMOOTHER_API_URL } from "src/lib/constants"
import useSWR from "swr"
import type {
  Pagination,
  StandingOrderInvoice,
  StandingOrderInvoiceFormValues,
  StandingOrderInvoiceListItem,
} from "../types"
import { toDateTimeOrNull, toStringOrNull } from "src/features/admin/screen-shared"

const ENDPOINT = `${SMOOTH_SMOOTHER_API_URL}/screen/standing_order_invoices`

/** 口座振替請求の一覧。オーダー ID を渡すと完全一致で絞り込む */
export const useStandingOrderInvoices = (page: number, orderId: string) => {
  const { getWithToken } = useSmoothApi()
  const query = new URLSearchParams({ page: String(page), ...(orderId ? { orderId } : {}) })
  const { data, error, isLoading } = useSWR(
    `${ENDPOINT}?${query.toString()}`,
    async (url: string) =>
      (await getWithToken(url)).data as {
        standingOrderInvoices: StandingOrderInvoiceListItem[]
        pagination: Pagination
      },
    { revalidateOnFocus: false }
  )

  return {
    standingOrderInvoices: data?.standingOrderInvoices ?? [],
    pagination: data?.pagination ?? null,
    error: error ?? null,
    isLoading,
  }
}

export const useStandingOrderInvoice = (id: number | null) => {
  const { getWithToken } = useSmoothApi()
  const { data, error, isLoading, mutate } = useSWR(
    id ? `${ENDPOINT}/${id}` : null,
    async (url: string) =>
      (await getWithToken(url)).data as { standingOrderInvoice: StandingOrderInvoice },
    { revalidateOnFocus: false }
  )

  return {
    standingOrderInvoice: data?.standingOrderInvoice ?? null,
    error: error ?? null,
    isLoading,
    mutate,
  }
}

const toMessage = (e: unknown, fallback: string) =>
  isAxiosError(e) && typeof e.response?.data?.message === "string"
    ? e.response.data.message
    : fallback

const toRequestBody = (values: StandingOrderInvoiceFormValues) => ({
  userId: toStringOrNull(values.userId),
  paymentPromiseId: toStringOrNull(values.paymentPromiseId),
  orderId: toStringOrNull(values.orderId),
  scheduledOn: toDateTimeOrNull(values.scheduledOn),
  amount: toStringOrNull(values.amount),
  queueStatus: values.queueStatus,
  acceptFromOn: toDateTimeOrNull(values.acceptFromOn),
  acceptToOn: toDateTimeOrNull(values.acceptToOn),
  resultOn: toDateTimeOrNull(values.resultOn),
  bankAccountId: toStringOrNull(values.bankAccountId),
})

type SaveResult<T> = { ok: true; value: T } | { ok: false; message: string }

/** 口座振替請求の作成・更新（admin と同じく削除は無い）。失敗したときは API のメッセージを返す */
export const useSaveStandingOrderInvoice = () => {
  const { postWithToken, patchWithToken } = useSmoothApi()
  const [isSaving, setIsSaving] = useState(false)

  const run = useCallback(
    async <T>(request: () => Promise<T>, fallback: string): Promise<SaveResult<T>> => {
      setIsSaving(true)
      try {
        return { ok: true, value: await request() }
      } catch (e) {
        return { ok: false, message: toMessage(e, fallback) }
      } finally {
        setIsSaving(false)
      }
    },
    []
  )

  const create = useCallback(
    (values: StandingOrderInvoiceFormValues) =>
      run(async () => {
        const response = await postWithToken(ENDPOINT, toRequestBody(values))
        return (response.data as { id: number }).id
      }, "作成に失敗しました"),
    [postWithToken, run]
  )

  const update = useCallback(
    (id: number, values: StandingOrderInvoiceFormValues) =>
      run(() => patchWithToken(`${ENDPOINT}/${id}`, toRequestBody(values)), "保存に失敗しました"),
    [patchWithToken, run]
  )

  return { create, update, isSaving }
}
